import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import {
    useDispatch,
    useSelector,
} from 'react-redux'
import { Home, Users, Briefcase, Wrench, Shield, User, LogOut, LucideIcon } from 'lucide-react'

import Column from '@amzn/meridian/column'
import Row from '@amzn/meridian/row'
import Sheet from '@amzn/meridian/sheet'
import Button from '@amzn/meridian/button'
import Icon from '@amzn/meridian/icon'
import chevronLeftSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-left-small'
import closeSmallTokens from '@amzn/meridian-tokens/base/icon/close-small'
import Table, {
    TableRow,
    TableCell,
} from '@amzn/meridian/table'

import { EnvironmentDetail, LODChecklistService } from '@amzn/ihm-associate-tools-js-lib'

import SideBarFooter from '@components/SideBar/SideBarFooter'
import getSideBarHeader from '@components/SideBar/SideBarHeader'
import getSideBarNavigation from '@components/SideBar/SideBarNavigation'
import {
    getSubzone,
    getZone,
} from '@atredux/selectors/zoneSelector'
import { getIsSeawardModalOpen } from '@atredux/selectors/iframeSelector'
import { setZoneInfoReducer } from '@atredux/slices/zoneSlice'
import {
    sideBarConfig,
    SideBarMode,
} from '@utils/ui-config'
import { getToolMenuConfig, getToolDetailsById, operationsTool, seawardReactTool, seawardTool } from '@services/ToolMenuConfigService'
import { getZoneTaskCount, setZoneAndSubZone, shouldShowZoneTasks as shouldShowZoneTasksCall } from '@crossframe/apis/ToolMenu'
import { getToolById } from '@crossframe/apis/ToolRegistration'
import { isFieldTech, onCurrentUserLoaded } from '@api/userAndStoreContext'
import { _navigateToTool } from '@components/SideBar/utils/SideBarUtil'
import { authService } from '@services/auth/AuthService'
import { useLogout } from '@hooks/useLogout'

import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import getSideBarFrequentlyUsedTools from './SideBarFrequentlyUsedTools'
import { getStoreInfo, isConfigured } from '@atredux/selectors/storeSelector'
import { getSelectedTool } from '@atredux/selectors/toolSelector'
import { getNotificationSheetState } from '@atredux/selectors/notificationSelector'
import { ITool } from '@atredux/slices/toolSlice'

import './SideBar.scss'

interface ISideBarProps {
    zoneToSubzoneMap: Map<string, string[]>
    subzoneToToolMap: Map<string, string[]>
    isMenuOpen: boolean
    toggleMenu: () => void
    toggleMenuButton: () => void
    setChangeLanguageOpen: (open: boolean) => void,
    isFrequentlyUsedToolsEnabled: boolean,
    displayNameOverrides?: Map<string, string>,
    platformMode?: 'meridian' | 'cloudscape',
}

interface IPortalNavItem {
    id: string
    labelStringId: string
    icon: LucideIcon
    toolName: string | undefined
    displayName: string
    hashPath: string | undefined
    toolParameters?: Record<string, string>
}

export const isZoneSelectMode = (sideBarMode) => sideBarMode === SideBarMode.ZONE_SELECT
export const isToolSelectMode = (sideBarMode) => sideBarMode === SideBarMode.TOOL_SELECT

/**
 * Pure derivation of the cloudscape rail's effective visual state.
 *
 * The rail is visually expanded if the user has pinned it open (`isMenuOpen`) OR the
 * pointer is transiently hovering it (`hoverExpanded`). Hover never mutates the persistent
 * `isMenuOpen` flag — this helper only combines the two inputs for render purposes.
 *
 * @param {boolean} isMenuOpen Persistent expand/collapse flag owned by ToolLayout.
 * @param {boolean} hoverExpanded Transient hover expansion local to SideBar.
 * @returns {boolean} True when the rail should render expanded.
 */
export const deriveIsVisuallyExpanded = (isMenuOpen: boolean, hoverExpanded: boolean): boolean =>
    isMenuOpen || hoverExpanded

// Collapse delay (ms) for the cloudscape hover-expand behavior — within the required [200, 300] window.
const COLLAPSE_DELAY_MS = 250
// Asset of the retired Sales page (the old dashboard). No longer in the cloudscape rail; a `?tool=`
// deep link to it lands on Home there instead of reopening it. The meridian layout is out of scope:
// the asset is registered for cloudscape only, so meridian resolves it like any other tool id.
const RETIRED_SALES_TOOL_ID = 'jwo-store-management-portal'

// Mobile breakpoint — at or below this width, cloudscape hover logic is bypassed.
const MOBILE_BREAKPOINT_PX = 768

// SSR / non-DOM safe check for a mobile-sized viewport. Used to bypass hover logic on handhelds.
const isMobileViewport = () =>
    typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT_PX

/**
 * Renders the sidebar and all its subcomponents. It holds sideBarMode
 * as state which determines zone selection or tool selection within the current
 * UI workflow. showAllTools is a flag to determine if tools for a zone should be
 * displayed instead of all possible tools. Zone and subzone state pull from Redux
 * globals and are set through action dispatch.
 *
 * Usage:
 * <SideBar
 *     zoneToSubzoneMap={zoneToSubzoneMap}
 *     subzoneToToolMap={subzoneToToolMap}
 *     isMenuOpen={true}
 *     toggleMenu={toggleMenu}
 *     isFrequentlyUsedToolsEnabled={true}
 * />
 *
 * @param {Map} zoneToSubzoneMap Mapping between zones and subzones.
 * @param {Map} subzoneToToolMap Mapping between subzones and their associated tool names.
 * @param {boolean} isMenuOpen Flag to decide if sidebar should be open.
 * @param {Function} toggleMenu Function to toggle menu open/closed.
 * @param {boolean} isFrequentlyUsedToolsEnabled Flag that decides if the frequently used tools menu layout is enabled.
 * @returns {React.ReactElement}
 */
const EMPTY_OVERRIDES_MAP = new Map<string, string>()

const SideBar = ({
    zoneToSubzoneMap,
    subzoneToToolMap,
    isMenuOpen,
    toggleMenu,
    toggleMenuButton,
    setChangeLanguageOpen,
    isFrequentlyUsedToolsEnabled,
    platformMode = 'meridian',
    displayNameOverrides = EMPTY_OVERRIDES_MAP,
}: Readonly<ISideBarProps>): React.ReactElement => {
    const [sideBarMode, setSideBarMode] = useState(SideBarMode.TOOL_SELECT)
    const [showAllTools, setShowAllTools] = useState(true)
    const selectedTool = useSelector(getSelectedTool)
    const isNotificationsOpen = useSelector(getNotificationSheetState)
    // Initialize wasZoneSelected based on selectedTool
    const [wasZoneSelected, setWasZoneSelected] = useState(() => {
        // Keep menu closed if initial tool is "Tasks"
        console.log('Setting wasZoneSelected state')
        if (selectedTool && selectedTool.displayName === 'Tasks') {
            /* For Tasks home page Associate doesnot have to select zone and subzone
            they will be redirected from the task to tool and zone information will be set
            at that time
             */
            console.log('Setting wasZoneSelected state to true')
            return true
        }
        // Default behavior - open for Welcome or any other case
        console.log('Setting wasZoneSelected state to false')
        return false
    })
    const [shouldShowZoneTasks, setShouldShowZoneTasks] = useState(false)
    const [zoneTaskCount, setZoneTaskCount] = useState(0)
    // Field tech (TechInstaller) gets a Tech-BringUp-only portal menu. Seeded from the module-level
    // current-user cache and refreshed via onCurrentUserLoaded, since that fetch is async and may not
    // be ready on first render.
    const [isFieldTechUser, setIsFieldTechUser] = useState(isFieldTech)

    // Signed-in user profile (name/email) for the cloudscape rail footer. Resolved from the OIDC
    // ID token claims via AuthService.getUserProfile() — the same source NavBarProfile uses.
    const [userProfile, setUserProfile] = useState<{ name: string; email: string }>({ name: '', email: '' })

    // Transient hover expansion for the cloudscape rail — independent from the persistent isMenuOpen flag.
    const [hoverExpanded, setHoverExpanded] = useState(false)
    // Handle for the pending collapse timer so we can cancel on re-entry / unmount.
    const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const [bundle] = useBundle(BUNDLE_NAME)

    const dispatch = useDispatch()

    // Unified sign-out for the cloudscape rail footer — same hook NavBarProfile uses.
    const logout = useLogout()

    const toggleMenuOnClose = useCallback(() => {
        toggleMenu()
    }, [toggleMenu])

    // Collapse the cloudscape rail on Nav_Item selection. Guarded so a selection while already
    // collapsed does not accidentally re-toggle the persistent flag back open (double-toggle guard).
    const collapseSidebar = useCallback(() => {
        if (isMenuOpen) {
            toggleMenu()
        }
    }, [isMenuOpen, toggleMenu])

    // Explicit collapse handler for the expanded-header collapse button. Unlike toggleMenuOnClose,
    // this only flips the persistent flag when it is actually open (so a click while merely
    // hover-expanded does not pin the rail open), and always clears the transient hover expansion.
    const handleCollapseClick = useCallback(() => {
        if (isMenuOpen) {
            toggleMenu()
        }
        setHoverExpanded(false)
    }, [isMenuOpen, toggleMenu])

    const zone = useSelector(getZone)
    const subzone = useSelector(getSubzone)
    const storeInfo = useSelector(getStoreInfo)
    // True once domain/region provisioning has completed. The field-tech auto-open below waits on
    // this so it never navigates against an unpopulated tool registry (which yields a blank iframe).
    const configured = useSelector(isConfigured)

    const _handleZoneSelection = (newZone: string, newSubzone: string) => {
        if (zone !== newZone) {
            LODChecklistService.changeZone(newZone)
        }

        dispatch(setZoneInfoReducer({
            zone: {
                zoneName: newZone,
                zoneDisplayName: newSubzone,
                subzones: [],
            },
            subzone: {
                subzoneName: newSubzone,
                subzoneDisplayName: newSubzone,
                tools: [],
            },
        }))

        setWasZoneSelected(true)

        const zoneObj = (getToolMenuConfig() || []).find((x) => x.zoneName === newZone) || ''
        const subzoneObj = zoneObj ? (zoneObj.subzones || []).find((x) => x.subzoneName === newSubzone) : ''
        setZoneAndSubZone(zoneObj, subzoneObj)
        setSideBarMode(SideBarMode.TOOL_SELECT)
    }

    const _selectInitialSubzone = () => {
        const initialZoneInfo = EnvironmentDetail.getInitialZoneInfo()
        if (!initialZoneInfo) {
            return
        }

        const zoneObj = (getToolMenuConfig() || []).find((x) => x.zoneName === initialZoneInfo.zoneName)
        if (!zoneObj) {
            console.warn('Initial zone not found in tool menu configuation: ' + initialZoneInfo.zoneName)
            return
        }

        const subzoneObj = (zoneObj.subzones || []).find((x) => x.subzoneName === initialZoneInfo.subzoneName)
        if (!subzoneObj) {
            console.warn('Initial subzone not found in tool menu configuration: ' + initialZoneInfo.subzoneName)
            return
        }

        console.info('JWOStoreManagementPlatform: Selecting initial zone: ' + zoneObj.zoneName + ', subzone: ' + subzoneObj.subzoneName)
        _handleZoneSelection(zoneObj.zoneName, subzoneObj.subzoneName)
    }

    const _handleChangeClick = () => {
        dispatch(setZoneInfoReducer({
            zone: {
                zoneName: '',
                zoneDisplayName: '',
                subzones: [],
            },
            subzone: {
                subzoneName: '',
                subzoneDisplayName: '',
                tools: [],
            },
        }))

        setShowAllTools(false)
        setSideBarMode(SideBarMode.ZONE_SELECT)
    }

    const _selectInitialTool = () => {
        const initialTool = EnvironmentDetail.getInitialTool()
        if (!initialTool) {
            return
        }
        if (platformMode === 'cloudscape' && initialTool === RETIRED_SALES_TOOL_ID) {
            console.info('JWOStoreManagementPlatform: Initial tool ' + initialTool + ' (Sales) was removed from the portal rail, staying on Home')
            return
        }

        const tool = getToolDetailsById(initialTool)
        if (tool) {
            setWasZoneSelected(true)
            toggleMenu()
            _navigateToTool(tool)
        } else {
            console.warn('Initial tool not found in tool menu configuration: ' + initialTool)
        }
    }

    useEffect(() => {
        shouldShowZoneTasksCall().then((result) => {
            console.log('JWOStoreManagementPlatform: Should show zone tasks: ' + result)
            setShouldShowZoneTasks(result as boolean)
        })

        getZoneTaskCount().then((result) => {
            console.log('JWOStoreManagementPlatform: Zone task count: ' + result)
            setZoneTaskCount(result as number)
        })
    }, [zone, subzone])

    /**
     * Called when the value of isFrequentlyUsedToolsEnabled changes.
     * Resets zone/subzone state when disabled.
     */
    useEffect(() => {
        if (!isFrequentlyUsedToolsEnabled) {
            console.log('JWOStoreManagementPlatform: isFrequentlyUsedToolsEnabled was disabled. Resetting zone state.')
            dispatch(setZoneInfoReducer({
                zone: {
                    zoneName: '',
                    zoneDisplayName: '',
                    subzones: [],
                },
                subzone: {
                    subzoneName: '',
                    subzoneDisplayName: '',
                    tools: [],
                },
            }))
        }
    }, [isFrequentlyUsedToolsEnabled])

    useEffect(() => {
        _selectInitialSubzone()
        _selectInitialTool()
    }, [])

    // Refresh the field-tech flag once the async current-user fetch completes (no-op if already loaded).
    useEffect(() => {
        onCurrentUserLoaded(() => setIsFieldTechUser(isFieldTech()))
    }, [])

    // Resolve the signed-in user's name/email for the cloudscape rail footer. Only needed in
    // cloudscape mode; failures are non-fatal (the footer simply renders without text).
    useEffect(() => {
        if (platformMode !== 'cloudscape') {
            return undefined
        }
        let isMounted = true
        authService.getUserProfile()
            .then((resolved) => {
                if (isMounted) setUserProfile(resolved)
            })
            .catch((err) => {
                console.warn('[SideBar] Could not resolve user profile:', err)
            })
        return () => {
            isMounted = false
        }
    }, [platformMode])

    // TODO: https://app.asana.com/1/8442528107068/project/1213997537694234/task/1215183552021118?focus=true
    // `labelStringId` is the Panther identifier of the visible label; `displayName`
    // stays in English because it is a tool-registry key matched against
    // selectedTool.displayName, not something the associate sees.
    const PORTAL_NAV_ITEMS: IPortalNavItem[] = [
        { id: 'home', labelStringId: 'jsma-web-common-home', icon: Home, toolName: undefined, displayName: 'Welcome', hashPath: undefined },
        // Operations renders the in-portal OperationsPage (a Cloudscape tabs page), not a tool iframe.
        { id: 'operations', labelStringId: 'jsma-web-operations-title', icon: Briefcase, toolName: undefined, displayName: operationsTool.displayName, hashPath: undefined },
        { id: 'team', labelStringId: 'jsma-web-common-team', icon: Users, toolName: 'jwo-user-management-tool', displayName: 'User Management', hashPath: undefined, toolParameters: { ds: 'cloudscape' } },
    ]

    // Rendered at the bottom of the rail, just above the signed-in user's profile, rather than
    // with the page items above. `displayName` stays 'Configurations': it is the registry key.
    const PORTAL_BOTTOM_NAV_ITEMS: IPortalNavItem[] = [
        { id: 'settings', labelStringId: 'jsma-web-sidebar-settings', icon: Shield, toolName: 'settings-navigator', displayName: 'Configurations', hashPath: undefined },
    ]

    // Field techs (TechInstaller) get a focused, Tech-BringUp-only menu (the tool also auto-opens on
    // load, see _selectInitialTool). Everyone else gets the standard portal menu above, which does not
    // include Tech BringUp — it is field-tech-only.
    const FIELD_TECH_PORTAL_NAV_ITEMS: IPortalNavItem[] = [
        { id: 'tech-bringup', labelStringId: 'jsma-web-sidebar-techBringUp', icon: Wrench, toolName: 'ihm-guided-bringup-dashboard', displayName: 'Tech BringUp', hashPath: undefined },
    ]
    const visiblePortalNavItems = isFieldTechUser ? FIELD_TECH_PORTAL_NAV_ITEMS : PORTAL_NAV_ITEMS
    const visibleBottomNavItems = isFieldTechUser ? [] : PORTAL_BOTTOM_NAV_ITEMS

    const selectedPortalNav = [...visiblePortalNavItems, ...visibleBottomNavItems].find((item) =>
        item.id === 'home'
            ? selectedTool.displayName === 'Welcome'
            : item.id === 'operations'
                ? selectedTool.displayName === operationsTool.displayName
                : selectedTool.toolName === item.toolName && selectedTool.hashPath === item.hashPath,
    )?.id || 'home'

    // Navigate to the selected Nav_Item's tool, then collapse the cloudscape rail. Clears the
    // transient hover expansion first so the rail does not stay visually expanded after selection.
    const handleNavItemSelect = useCallback((item: IPortalNavItem) => {
        if (item.id === 'home') {
            _navigateToTool(seawardTool)
        } else if (item.id === 'operations') {
            _navigateToTool(operationsTool)
        } else {
            const tool: ITool = {
                displayName: item.displayName,
                displayNameStringId: '',
                metadata: { cti: '', package: '' },
                path: '',
                toolName: item.toolName,
                isMigrated: false,
                showMigrated: true,
                hashPath: item.hashPath,
                skipStoreScan: true,
                toolParameters: item.toolParameters,
            }
            _navigateToTool(tool)
        }
        setHoverExpanded(false)
        collapseSidebar()
    }, [collapseSidebar])

    // Field techs auto-open Tech BringUp (their only tool) whenever they land on Welcome. Gated on
    // `configured` so the nav never runs against an unpopulated tool registry, which would render a
    // blank iframe on refresh. Deliberately ignores getInitialTool(): a stale deep link must not
    // strand a field tech on Welcome.
    useEffect(() => {
        if (isFieldTechUser && configured && selectedTool.displayName === 'Welcome') {
            handleNavItemSelect(FIELD_TECH_PORTAL_NAV_ITEMS[0])
        }
    }, [isFieldTechUser, configured, selectedTool.displayName])

    // Pointer entered the cloudscape rail: cancel any pending collapse and transiently expand.
    // Bypassed on mobile (tap-to-toggle only). Only expands when persistently collapsed.
    const handleMouseEnter = useCallback(() => {
        if (isMobileViewport()) {
            return
        }
        // Cancel any pending collapse from a previous leave.
        if (collapseTimerRef.current !== null) {
            clearTimeout(collapseTimerRef.current)
            collapseTimerRef.current = null
        }
        // Transiently expand only when persistently collapsed.
        if (!isMenuOpen) {
            setHoverExpanded(true)
        }
    }, [isMenuOpen])

    // Pointer left the cloudscape rail: schedule a debounced return to collapsed.
    // Bypassed on mobile. Cancelled if the pointer re-enters before COLLAPSE_DELAY_MS.
    const handleMouseLeave = useCallback(() => {
        if (isMobileViewport()) {
            return
        }
        if (collapseTimerRef.current !== null) {
            clearTimeout(collapseTimerRef.current)
        }
        collapseTimerRef.current = setTimeout(() => {
            setHoverExpanded(false)
            collapseTimerRef.current = null
        }, COLLAPSE_DELAY_MS)
    }, [])

    // Clear any pending collapse timer on unmount so a stale timer never fires.
    useEffect(() => {
        return () => {
            if (collapseTimerRef.current !== null) {
                clearTimeout(collapseTimerRef.current)
                collapseTimerRef.current = null
            }
        }
    }, [])

    // Effective visual state for the cloudscape rail: expanded if pinned open OR transiently hovered.
    const isVisuallyExpanded = deriveIsVisuallyExpanded(isMenuOpen, hoverExpanded)

    const renderNavItem = (item: IPortalNavItem) => (
        <button
            key={item.id}
            className={`portal-sidebar-nav-btn ${item.id === selectedPortalNav ? 'selected' : ''}`}
            data-atb-id={`portal-nav-${item.id}`}
            onClick={() => handleNavItemSelect(item)}
        >
            <item.icon size={20} />
            {isVisuallyExpanded && <span className="portal-sidebar-label">{t(bundle, item.labelStringId)}</span>}
        </button>
    )

    return (
        <Sheet
            data-atb-id={'side-menu-sheet-id'}
            type={sideBarConfig.SIDEBAR_TYPE}
            side={sideBarConfig.SIDEBAR_POSITION}
            open={platformMode === 'cloudscape' ? true : isMenuOpen}
            onClose={platformMode === 'cloudscape' ? undefined : toggleMenuOnClose}
            spacingInset={sideBarConfig.SIDEBAR_PADDING}
            className={isNotificationsOpen ? 'sidebar-above-notifications' : undefined}
        >
            {platformMode === 'cloudscape' ? (
                <div
                    className={`portal-sidebar-content ${isVisuallyExpanded ? 'portal-sidebar-open' : 'portal-sidebar-closed'}`}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    <div className="portal-sidebar-top">
                        {isVisuallyExpanded ? (
                            <div className="portal-sidebar-header">
                                <div className="portal-sidebar-logo">
                                    <img src={require('@src/assets/jwo-logo-light.png')} alt={t(bundle, 'jsma-web-common-jwoLogoAlt')} className="portal-sidebar-logo-image" />
                                </div>
                                <button className="portal-sidebar-close-btn" onClick={handleCollapseClick} aria-label={t(bundle, 'jsma-web-common-collapseSidebar')}>
                                    <Icon tokens={chevronLeftSmallTokens}>{t(bundle, 'jsma-web-common-collapse')}</Icon>
                                </button>
                            </div>
                        ) : (
                            <button className="portal-sidebar-logo-btn" onClick={toggleMenuOnClose}>
                                <span className="portal-sidebar-logo-text">JWO</span>
                            </button>
                        )}
                        <div className="portal-sidebar-divider" />
                    </div>
                    <div className="portal-sidebar-middle">
                        {visiblePortalNavItems.map(renderNavItem)}
                    </div>
                    <div className="portal-sidebar-bottom">
                        <div className="portal-sidebar-divider" />
                        {visibleBottomNavItems.length > 0 && (
                            <div className="portal-sidebar-bottom-nav">
                                {visibleBottomNavItems.map(renderNavItem)}
                            </div>
                        )}
                        <div className="portal-sidebar-user" data-atb-id="portal-sidebar-user">
                            <div className="portal-sidebar-user-avatar" aria-hidden="true">
                                <User size={18} />
                            </div>
                            {isVisuallyExpanded && (userProfile.name || userProfile.email) && (
                                <div className="portal-sidebar-user-info">
                                    {userProfile.name && (
                                        <span className="portal-sidebar-user-name" data-atb-id="portal-sidebar-user-name">
                                            {userProfile.name}
                                        </span>
                                    )}
                                    {userProfile.email && (
                                        <span className="portal-sidebar-user-email" data-atb-id="portal-sidebar-user-email">
                                            {userProfile.email}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        {isVisuallyExpanded && (
                            <button
                                className="portal-sidebar-signout-btn"
                                data-atb-id="portal-sidebar-signout"
                                onClick={logout}
                                aria-label="Sign out"
                            >
                                <LogOut size={18} />
                                <span className="portal-sidebar-signout-label">Sign out</span>
                            </button>
                        )}
                    </div>
                </div>
            ) : (
            <Column
                alignmentVertical={sideBarConfig.SIDEBAR_VERTICAL_ALIGNMENT}
                spacing="none"
            >
                <Column width={sideBarConfig.SIDEBAR_WIDTH} data-atb-id="sidebarMenu" spacing="none">
                    {/* Spacer matching navbar height so sidebar content starts below the elevated navbar */}
                    <div className="sidebar-navbar-spacer" />
                    <Row alignmentHorizontal="end" spacingInset="300 300 none none">
                        <Button
                            type="icon"
                            size="small"
                            onClick={toggleMenuOnClose}
                            data-atb-id="sidebar-close-button"
                        >
                            <Icon tokens={closeSmallTokens}>Close</Icon>
                        </Button>
                    </Row>
                    <Table
                        headerRows={0}
                        showDividers={true}
                    >
                        {getSideBarNavigation(
                            sideBarMode,
                            showAllTools,
                            setShowAllTools,
                            zoneToSubzoneMap,
                            subzoneToToolMap,
                            zone,
                            subzone,
                            _handleZoneSelection,
                            toggleMenu,
                            toggleMenuButton,
                            t,
                            bundle,
                            isFrequentlyUsedToolsEnabled,
                            displayNameOverrides,
                        )}

                        {isToolSelectMode(sideBarMode) && getToolById('zone_tasks_tool') && shouldShowZoneTasks &&
                            <TableRow
                                data-atb-id={`sideBar-zone_tasks_tool`}
                                key={`sideBar-zone_tasks_tool-${Math.floor((Math.random() * 10000) + 1)}`}
                                onClick={() => _navigateToTool(getToolById('zone_tasks_tool'), toggleMenu)}
                            >
                                <TableCell>
                                    {`${t(bundle, 'migrated-ihm-seaward-zone-tasks-label')} (${zoneTaskCount})`}
                                </TableCell>
                            </TableRow>
                        }
                    </Table>
                </Column>

                <Column width={sideBarConfig.SIDEBAR_WIDTH} data-atb-id="sidebarFooter" >
                    <SideBarFooter
                        zone={zone}
                        showAllTools={showAllTools}
                        setShowAllTools={setShowAllTools}
                        isMenuOpen={isMenuOpen}
                        toggleMenu={toggleMenu}
                        sideBarMode={sideBarMode}
                        setChangeLanguageOpen={setChangeLanguageOpen}
                        isFrequentlyUsedToolsEnabled={isFrequentlyUsedToolsEnabled}
                        t={t}
                        bundle={bundle}
                    />
                </Column>
            </Column>
            )}
        </Sheet>
    )
}

export default SideBar
